import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import type { Buzz } from '../../lib/types'
import './buzz.css'

// Reacciones al estilo de los boosts de Basecamp; aquí se llaman BUZZ (de zumbido)
// — Damián, 9-sep-2026. Un afford. claro y SIEMPRE visible debajo de cada
// interacción (mensaje, comentario, post, nota, chat). Las reacciones ya puestas se
// ven siempre como pills; un botón discreto pero permanente abre el selector con
// emojis rápidos + texto libre (≤15). UN buzz por usuario por entidad
// (comunicacion.md §Buzz). Componente presentacional: el estado del popover vive
// aquí, pero la mutación (optimistic + rollback + invalidación) la maneja cada
// consumidor vía onBuzz. onBuzz(null) = quitar el propio; onBuzz(texto) = poner.
//
// NOMBRES INTERNOS: el tipo `Buzz`, el campo `buzzes`, las rutas PUT …/buzz y las
// clases CSS .buzz-* ya se llamaban así. Lo único que cambió en v2 son las
// ETIQUETAS VISIBLES (decían "Boost"), que viven todas en este archivo.

export const MAX_BUZZ_LEN = 15

// Emojis de acceso rápido: un clic pone ese buzz de inmediato (reemplaza el propio).
const QUICK_EMOJIS = ['👍', '🙌', '🔥', '😂', '❤️', '🐝', '✅', '👀', '🎉', '🙏']

// Cuenta codepoints (no unidades UTF-16): un emoji cuenta como UN carácter. Se usa
// tanto para el contador como para recortar en el onChange (paste incluido).
const cp = (s: string): string[] => [...s]

/**
 * ¿Es MI buzz de camp? Un boost importado de Basecamp (`bcBoostId`) con mi nombre
 * no lo es: es historia, se pinta como los de los demás y no se edita ni se quita.
 * Basecamp dejaba varios por persona, así que tratarlo como "el mío" hacía que
 * quitar el propio se llevara también los importados.
 */
export const esBuzzPropio = (b: Buzz, me: string): boolean => b.by === me && b.bcBoostId == null

// Espejo cliente de services/buzz.applyBuzz — para el optimistic update de cada
// consumidor: saca el buzz propio y, si hay texto, lo re-agrega al final.
export function applyBuzzLocal(
  existing: Buzz[] | undefined,
  meEmail: string,
  meName: string,
  text: string | null,
): Buzz[] {
  const others = (existing ?? []).filter((b) => !esBuzzPropio(b, meEmail))
  if (text == null || text.trim() === '') return others
  return [...others, { by: meEmail, byName: meName, text: text.trim(), at: new Date().toISOString() }]
}

/** El panal (hexágono) con "+" cuando todavía no dejaste ninguno. */
export function GlifoBuzz({ conMas }: { conMas?: boolean }) {
  return (
    <svg className="buzz-hex" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M12 3.2 19 7.1v7.8L12 18.8 5 14.9V7.1z" />
      {conMas && <path d="M12 9.2v5.6M9.2 12h5.6" strokeWidth="2" />}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// El selector (popover)
// ---------------------------------------------------------------------------

/**
 * El selector de reacciones. Se extrajo de `BuzzBar` para que la BARRA DE ACCIONES
 * al hover (diseño v2 §3.3) abra EXACTAMENTE el mismo popover desde su propio
 * botón, en vez de tener dos selectores distintos que se parecen.
 *
 * `arriba` lo ancla hacia arriba (su sitio natural bajo un mensaje); en la barra de
 * acciones, que ya flota sobre la fila, se despliega hacia abajo. `der` alinea el
 * panel por su borde derecho: solo lo quiere la barra de acciones, que vive
 * pegada a la derecha de la fila. Alineado a la izquierda —el default— el panel
 * cae bajo el botón que lo abrió, que es lo correcto para una barra inline.
 */
export function BuzzPop({
  mine,
  onPut,
  arriba = true,
  der = false,
}: {
  mine: Buzz | null
  onPut: (text: string | null) => void
  arriba?: boolean
  der?: boolean
}) {
  const [value, setValue] = useState(mine?.text ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const commit = () => {
    const trimmed = value.trim()
    // Vacío = quitar el propio (solo tiene efecto si ya había uno).
    onPut(trimmed === '' ? null : trimmed)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    }
  }

  const remaining = MAX_BUZZ_LEN - cp(value).length

  return (
    <div
      className={`pop buzz-pop${arriba ? '' : ' abajo'}${der ? ' der' : ''}`}
      role="dialog"
      aria-label="Dejar un buzz"
    >
      <div className="buzz-pop-emojis">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className={`buzz-emoji${mine?.text === emoji ? ' on' : ''}`}
            title={`Buzz ${emoji}`}
            aria-label={`Buzz ${emoji}`}
            onClick={() => onPut(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
      <hr className="pop-sep" />
      <div className="buzz-pop-input-row">
        <input
          ref={inputRef}
          className="buzz-input"
          value={value}
          // Recorta a 15 codepoints (maxLength del DOM cuenta mal los emojis).
          onChange={(e) => setValue(cp(e.target.value).slice(0, MAX_BUZZ_LEN).join(''))}
          onKeyDown={onKeyDown}
          placeholder="Escribe un buzz…"
          aria-label="Escribe tu buzz (máximo 15 caracteres)"
        />
        <span className={`buzz-count${remaining <= 0 ? ' full' : ''}`} aria-hidden="true">
          {remaining}
        </span>
        <button
          type="button"
          className="buzz-mini ok"
          title="Guardar"
          aria-label="Guardar buzz"
          onClick={commit}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path d="M5 12l5 5L20 6" />
          </svg>
        </button>
      </div>
      {mine && (
        <>
          <hr className="pop-sep" />
          <div className="buzz-pop-current">
            <span className="buzz-pop-current-txt">
              Tu buzz: <strong>{mine.text}</strong>
            </span>
            <button
              type="button"
              className="buzz-remove"
              title="Quitar mi buzz"
              aria-label="Quitar mi buzz"
              onClick={() => onPut(null)}
            >
              Quitar
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Envoltura que abre/cierra un `BuzzPop` anclado a lo que se le pase como disparador.
 * La usan la barra de buzz y la barra de acciones al hover.
 */
export function BuzzPopWrap({
  mine,
  onBuzz,
  disabled,
  arriba,
  der,
  children,
}: {
  mine: Buzz | null
  onBuzz: (text: string | null) => void
  disabled?: boolean
  arriba?: boolean
  der?: boolean
  /** El disparador. Recibe si está abierto y la función para alternarlo. */
  children: (estado: { open: boolean; alternar: () => void }) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Cerrar al hacer clic afuera o con Escape (a nivel documento, para que también
  // atrape clics fuera de la barra).
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    // Escape en CAPTURA + preventDefault: el selector es la capa de más arriba, así
    // que se queda el Escape antes que nadie. Con burbujeo no bastaba — el panel del
    // chat escucha en el MISMO documento y, por haberse montado antes, cerraba el
    // chat completo junto con este popover. La marca de defaultPrevented es lo que
    // useCerrarAlClicFuera mira para no cerrarse encima.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const put = (text: string | null) => {
    onBuzz(text)
    setOpen(false)
  }

  return (
    <div className="buzz-pop-wrap" ref={wrapRef}>
      {children({ open, alternar: () => !disabled && setOpen((v) => !v) })}
      {open && <BuzzPop mine={mine} onPut={put} arriba={arriba} der={der} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// La barra
// ---------------------------------------------------------------------------

export interface BuzzBarProps {
  buzzes?: Buzz[]
  onBuzz: (text: string | null) => void
  me: string
  disabled?: boolean
  // dense: variante compacta para el chat (botón solo-icono, un poco más discreto).
  dense?: boolean
  /**
   * Hacia dónde se abre el selector. Por defecto ARRIBA, que es su sitio bajo un
   * mensaje largo. En un COMENTARIO no sirve: la barra va pegada al final de un
   * bloque corto, así que abrir hacia arriba tapaba el texto del propio
   * comentario al que se va a reaccionar y el encabezado "Comentarios (n)".
   * Ahí se pasa `arriba={false}` y el panel cae bajo el botón.
   */
  arriba?: boolean
}

export function BuzzBar({ buzzes, onBuzz, me, disabled, dense, arriba }: BuzzBarProps) {
  const list = buzzes ?? []
  const mine = list.find((b) => esBuzzPropio(b, me)) ?? null

  return (
    <BuzzPopWrap mine={mine} onBuzz={onBuzz} disabled={disabled} arriba={arriba}>
      {({ open, alternar }) => (
        <div className={`buzz-bar${dense ? ' dense' : ''}`}>
          {list.map((b) => {
            const own = esBuzzPropio(b, me)
            return (
              <button
                // NO `b.by`: los boosts importados traen varios de la misma persona
                // (228 documentos del ensayo) y la llave repetida hacía que React
                // pintara uno y descartara o mezclara los demás.
                key={b.bcBoostId ?? `${b.by}-${b.at}`}
                type="button"
                className={`buzz-pill${own ? ' own' : ''}`}
                title={`${b.byName} · ${b.text}`}
                disabled={disabled || !own}
                onClick={own ? alternar : undefined}
                aria-label={own ? `Tu buzz: ${b.text} (clic para editar)` : `Buzz de ${b.byName}: ${b.text}`}
              >
                {b.text}
              </button>
            )
          })}

          <button
            type="button"
            className={`buzz-boost${open ? ' active' : ''}${mine ? ' has' : ''}`}
            title={mine ? 'Editar tu buzz' : 'Dejar un buzz'}
            aria-label={mine ? 'Editar tu buzz' : 'Dejar un buzz'}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled}
            onClick={alternar}
          >
            <GlifoBuzz conMas={!mine} />
            {!dense && <span className="buzz-boost-label">Buzz</span>}
          </button>
        </div>
      )}
    </BuzzPopWrap>
  )
}
